import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Tabs,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createUsersApi } from "../api";
import {
  accountStatusTone,
  formatDateTime,
  formatLabel,
  timeAgo,
  verificationStatusTone,
} from "../labels";
import type {
  DataRequestType,
  DataRequestVia,
  NoteDto,
  RoleExtensionAction,
  RoleExtensionDto,
  UserDetailResponse,
  UserOverviewResponse,
  VerificationDto,
} from "../types";
import {
  BookingsPanel,
  BusinessesPanel,
  CommunitiesPanel,
  DataRequestsPanel,
  EnforcementPanel,
  NotesPanel,
  PlayProfileCard,
  ReferralsPanel,
  ReportsPanel,
  TicketsPanel,
  TimelinePanel,
  UserStats,
} from "../components/UserProfilePanels";
import { AccountActionDialog } from "../components/AccountActionDialog";
import {
  CreateRoleExtensionDialog,
  RoleExtensionActionDialog,
} from "../components/RoleExtensionDialogs";
import {
  DecideVerificationDialog,
  RequestVerificationDialog,
} from "../components/VerificationDialogs";
import { MergeUsersDialog } from "../components/MergeUsersDialog";
import { ProfileEditDrawer } from "../components/ProfileEditDrawer";

export function UserDetailPage() {
  const { userId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createUsersApi(client), [client]);
  const toast = useToast();

  const [detail, setDetail] = useState<UserDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [accountOpen, setAccountOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [roleAction, setRoleAction] = useState<{
    id: string;
    action: RoleExtensionAction;
  } | null>(null);
  const [requestVerificationOpen, setRequestVerificationOpen] = useState(false);
  const [decideVerification, setDecideVerification] =
    useState<VerificationDto | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [overview, setOverview] = useState<UserOverviewResponse | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [privacyError, setPrivacyError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") ?? "overview";

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Users", to: "/app/users" },
    {
      label:
        overview?.profile?.name ||
        detail?.user.display_name ||
        detail?.user.public_id ||
        "User Detail",
    },
  ]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      // The overview is the Play-side half of the picture. It failing must not
      // hide the account record, so it is read separately and reported.
      const [response, ov] = await Promise.all([
        api.getDetail(userId),
        api.getOverview(userId).then(
          (value) => ({ value, error: null as string | null }),
          (err: unknown) => ({ value: null, error: apiErrorMessage(err) }),
        ),
      ]);
      setDetail(response);
      setOverview(ov.value);
      setOverviewError(ov.error);
    } catch (err) {
      setError(apiErrorMessage(err));
      setDetail(null);
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  async function reloadOverview() {
    try {
      setOverview(await api.getOverview(userId));
      setOverviewError(null);
    } catch (err) {
      setOverviewError(apiErrorMessage(err));
    }
  }

  async function runExport() {
    if (!detail) return;
    setExporting(true);
    try {
      const result = await api.exportUser(detail.user.id);
      const blob = new Blob([JSON.stringify(result, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `huddl-user-${detail.user.public_id}-export.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.push({
        title: "Export downloaded",
        description: "The access was recorded in the audit log.",
        tone: "success",
      });
      await reloadOverview();
    } catch (err) {
      toast.push({
        title: "Export failed",
        description: apiErrorMessage(err),
        tone: "danger",
      });
    } finally {
      setExporting(false);
    }
  }

  async function addNote(body: string): Promise<boolean> {
    setBusy(true);
    setNoteError(null);
    try {
      await api.addNote(userId, { body });
      await reloadOverview();
      return true;
    } catch (err) {
      setNoteError(apiErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function pinNote(note: NoteDto) {
    try {
      await api.pinNote(userId, note.id, !note.pinned);
      await reloadOverview();
    } catch (err) {
      toast.push({ title: "Could not update note", description: apiErrorMessage(err), tone: "danger" });
    }
  }

  async function createDataRequest(input: {
    request_type: DataRequestType;
    received_via: DataRequestVia;
    due_at?: string | null;
  }): Promise<boolean> {
    setBusy(true);
    setPrivacyError(null);
    try {
      await api.createDataRequest(userId, input);
      toast.push({ title: "Data request logged", tone: "success" });
      await reloadOverview();
      return true;
    } catch (err) {
      setPrivacyError(apiErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function updateDataRequest(
    id: string,
    status: "in_progress" | "completed" | "rejected",
    note: string | null,
  ): Promise<boolean> {
    setBusy(true);
    setPrivacyError(null);
    try {
      await api.updateDataRequest(userId, id, { status, ...(note ? { note } : {}) });
      toast.push({ title: "Data request updated", tone: "success" });
      await reloadOverview();
      return true;
    } catch (err) {
      setPrivacyError(apiErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  const verifiedIndicator = useMemo(() => {
    const list = detail?.verifications ?? [];
    return list.some(
      (item) => item.status === "verified" || item.status === "accepted",
    );
  }, [detail]);

  if (loading) {
    return <LoadingState message="Loading User Detail…" />;
  }

  if (error || !detail) {
    return (
      <EmptyState
        title="User unavailable"
        description={error ?? "User not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const { user, role_extensions, verifications, auth_identities, device_sessions } =
    detail;
  const profile = overview?.profile ?? null;
  const title = profile?.name || user.display_name || "Unnamed User";
  const blocksPlay =
    user.account_status === "suspended" ||
    user.account_status === "banned" ||
    user.account_status === "closed";

  const overviewUnavailable = overviewError ? (
    <Alert tone="danger" title="Player activity could not be loaded">
      {overviewError}{" "}
      <Button size="sm" variant="secondary" onClick={() => void reloadOverview()}>
        Retry
      </Button>
    </Alert>
  ) : (
    <LoadingState message="Loading…" />
  );

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        description={
          profile
            ? `@${profile.handle} · Public ID ${user.public_id}`
            : `Public ID ${user.public_id}`
        }
        actions={
          <Flex gap={8} wrap>
            <Button variant="secondary" onClick={() => setProfileOpen(true)}>
              Edit profile
            </Button>
            <Button variant="secondary" onClick={() => setAccountOpen(true)}>
              Account actions
            </Button>
            <Button variant="secondary" onClick={() => setMergeOpen(true)}>
              Merge…
            </Button>
          </Flex>
        }
      />

      <ActionBar
        left={
          <Flex gap={8} align="center" wrap>
            <StatusChip tone={accountStatusTone(user.account_status)}>
              Account: {formatLabel(user.account_status)}
            </StatusChip>
            <Badge tone={verifiedIndicator ? "success" : "neutral"}>
              {verifiedIndicator ? "Identity verified" : "Identity not verified"}
            </Badge>
            {overview?.auth?.last_sign_in_at ? (
              <Badge tone="neutral">
                Last sign-in {timeAgo(overview.auth.last_sign_in_at)}
              </Badge>
            ) : null}
            {overview && overview.stats.open_data_requests > 0 ? (
              <Badge tone="warning">
                {overview.stats.open_data_requests} open data request
                {overview.stats.open_data_requests === 1 ? "" : "s"}
              </Badge>
            ) : null}
            {user.closed_at ? (
              <Badge tone="danger">Closed {formatDateTime(user.closed_at)}</Badge>
            ) : null}
          </Flex>
        }
      />

      {blocksPlay ? (
        <Alert tone="warning" title="This person is blocked from the app">
          Play shows an account-{user.account_status} screen and signs them out.
          Bookings and messages already made are unaffected.
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onValueChange={(next) => {
          const params = new URLSearchParams(searchParams);
          if (next === "overview") params.delete("tab");
          else params.set("tab", next);
          setSearchParams(params, { replace: true });
        }}
        items={[
          {
            id: "overview",
            label: "Overview",
            content: (
              <Stack gap={16}>
                <PlayProfileCard user={user} profile={profile} />
                {overview ? <UserStats stats={overview.stats} /> : overviewUnavailable}
                <Card header={<Text role="title" as="h3">Account</Text>}>
                  <Stack gap={8}>
                    <Text role="body" as="p">Email: {user.email ?? "—"}</Text>
                    <Text role="body" as="p">Phone: {user.phone ?? "—"}</Text>
                    <Text role="body" as="p">Market: {user.market_id ?? "—"}</Text>
                    {overview?.auth ? (
                      <>
                        <Text role="body" as="p">
                          Signed up: {formatDateTime(overview.auth.created_at)}
                        </Text>
                        <Text role="body" as="p">
                          Email confirmed:{" "}
                          {overview.auth.email_confirmed_at
                            ? formatDateTime(overview.auth.email_confirmed_at)
                            : "not confirmed"}
                        </Text>
                        <Text role="body" as="p">
                          Sign-in method: {overview.auth.providers.join(", ") || "—"}
                        </Text>
                      </>
                    ) : null}
                    {overview?.referral ? (
                      <Text role="body" as="p">
                        Referral code used: {overview.referral.code_used ? `@${overview.referral.code_used}` : "none"}
                        {" · "}Accounts referred: {overview.referral.referred_count}
                      </Text>
                    ) : null}
                    {overview?.creator ? (
                      <Text role="body" as="p">
                        Creator profile: {formatLabel(overview.creator.status)}
                        {overview.creator.current_tier
                          ? ` (${formatLabel(overview.creator.current_tier)})`
                          : ""}
                      </Text>
                    ) : null}
                    <Text role="caption" color="muted" as="p">
                      User ID: {user.id}
                    </Text>
                  </Stack>
                </Card>
                {overview && overview.notes.some((n) => n.pinned) ? (
                  <Alert tone="info" title="Pinned notes">
                    <Stack gap={4}>
                      {overview.notes
                        .filter((n) => n.pinned)
                        .map((n) => (
                          <Text key={n.id} role="small" as="p">
                            {n.body}
                          </Text>
                        ))}
                    </Stack>
                  </Alert>
                ) : null}
              </Stack>
            ),
          },
          {
            id: "activity",
            label: "Activity",
            content: overview ? (
              <Stack gap={24}>
                <Stack gap={8}>
                  <Text role="h2" as="h2">Bookings</Text>
                  <BookingsPanel
                    bookings={overview.bookings}
                    total={overview.stats.bookings_total}
                  />
                </Stack>
                <Stack gap={8}>
                  <Text role="h2" as="h2">Communities</Text>
                  <CommunitiesPanel communities={overview.communities} />
                </Stack>
                <Stack gap={8}>
                  <Text role="h2" as="h2">Businesses</Text>
                  <BusinessesPanel businesses={overview.businesses} />
                </Stack>
                <Stack gap={8}>
                  <Text role="h2" as="h2">Referrals</Text>
                  {overview.referral ? (
                    <ReferralsPanel referral={overview.referral} />
                  ) : (
                    <Text role="small" color="secondary" as="p">Referrals need the latest API.</Text>
                  )}
                </Stack>
              </Stack>
            ) : (
              overviewUnavailable
            ),
          },
          {
            id: "safety",
            label: `Safety${
              overview && overview.stats.reports_against > 0
                ? ` (${overview.stats.reports_against})`
                : ""
            }`,
            content: overview ? (
              <Stack gap={24}>
                <Stack gap={8}>
                  <Text role="h2" as="h2">Reports</Text>
                  <ReportsPanel
                    made={overview.reports_made}
                    against={overview.reports_against}
                  />
                </Stack>
                <Stack gap={8}>
                  <Text role="h2" as="h2">Enforcement</Text>
                  <EnforcementPanel rows={overview.enforcement} />
                </Stack>
                <Stack gap={8}>
                  <Text role="h2" as="h2">Support tickets</Text>
                  <TicketsPanel rows={overview.tickets} />
                </Stack>
              </Stack>
            ) : (
              overviewUnavailable
            ),
          },
          {
            id: "access",
            label: "Roles & verification",
            content: (
              <Stack gap={24}>
                <Surface>
                  <Stack gap={16}>
                    <Flex justify="space-between" align="center" wrap gap={8}>
                      <Text role="h2" as="h2">Role extensions</Text>
                      <Button size="sm" variant="secondary" onClick={() => setCreateRoleOpen(true)}>
                        Add role extension
                      </Button>
                    </Flex>
                    {role_extensions.length === 0 ? (
                      <EmptyState
                        title="No role extensions"
                        description="Role extensions remain separate from account status."
                        reason="none"
                      />
                    ) : (
                      <RoleExtensionsTable
                        rows={role_extensions}
                        onAction={(id, action) => setRoleAction({ id, action })}
                      />
                    )}
                  </Stack>
                </Surface>

                <Surface>
                  <Stack gap={16}>
                    <Flex justify="space-between" align="center" wrap gap={8}>
                      <Text role="h2" as="h2">Verifications</Text>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setRequestVerificationOpen(true)}
                      >
                        Request verification
                      </Button>
                    </Flex>
                    <Text role="small" color="secondary" as="p">
                      A verified identity gives the person the verified badge in
                      Play and tells them in their inbox.
                    </Text>
                    {verifications.length === 0 ? (
                      <EmptyState
                        title="No verification records"
                        description="Request identity or governed verification when authorized."
                        reason="none"
                      />
                    ) : (
                      <Table>
                        <Thead>
                          <Tr>
                            <Th>Type</Th>
                            <Th>Status</Th>
                            <Th>Requested</Th>
                            <Th>Reviewed</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {verifications.map((row) => (
                            <Tr key={row.id}>
                              <Td>{formatLabel(row.verification_type)}</Td>
                              <Td>
                                <StatusChip tone={verificationStatusTone(row.status)}>
                                  {formatLabel(row.status)}
                                </StatusChip>
                              </Td>
                              <Td>{formatDateTime(row.requested_at)}</Td>
                              <Td>{formatDateTime(row.reviewed_at)}</Td>
                              <Td>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => setDecideVerification(row)}
                                >
                                  Decide
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    )}
                  </Stack>
                </Surface>
              </Stack>
            ),
          },
          {
            id: "security",
            label: "Sign-in & devices",
            content: (
              <Stack gap={16}>
                <Card header={<Text role="title" as="h3">Sign-in identities</Text>}>
                  {auth_identities.length === 0 ? (
                    <Text role="small" color="secondary" as="p">
                      No linked sign-in identities. Guests created by a booking
                      have none until they sign up.
                    </Text>
                  ) : (
                    <Table>
                      <Thead>
                        <Tr>
                          <Th>Provider</Th>
                          <Th>Verified email</Th>
                          <Th>Verified phone</Th>
                          <Th>Primary</Th>
                          <Th>Last login</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {auth_identities.map((row) => (
                          <Tr key={row.id}>
                            <Td>{row.provider}</Td>
                            <Td>{row.verified_email ?? "—"}</Td>
                            <Td>{row.verified_phone ?? "—"}</Td>
                            <Td>{row.is_primary ? "Yes" : "No"}</Td>
                            <Td>{formatDateTime(row.last_login)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </Card>

                {device_sessions ? (
                  <Card header={<Text role="title" as="h3">Devices & sessions</Text>}>
                    {device_sessions.length === 0 ? (
                      <Text role="small" color="secondary" as="p">
                        No device sessions recorded. Play does not report devices
                        to HQ yet, so this stays empty until it does.
                      </Text>
                    ) : (
                      <Table>
                        <Thead>
                          <Tr>
                            <Th>Status</Th>
                            <Th>Fingerprint</Th>
                            <Th>Observed</Th>
                            <Th>Expires</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {device_sessions.map((row) => (
                            <Tr key={row.id}>
                              <Td>{formatLabel(row.status)}</Td>
                              <Td>{row.device_fingerprint ?? "—"}</Td>
                              <Td>{formatDateTime(row.observed_at)}</Td>
                              <Td>{formatDateTime(row.expires_at)}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    )}
                  </Card>
                ) : (
                  <Alert tone="info" title="Device & security hidden">
                    Device sessions and sign-in history are omitted when the
                    backend withholds security-classified fields for this actor.
                  </Alert>
                )}
              </Stack>
            ),
          },
          {
            id: "privacy",
            label: "Privacy",
            content: overview ? (
              <DataRequestsPanel
                requests={overview.data_requests}
                busy={busy}
                error={privacyError}
                exporting={exporting}
                onCreate={createDataRequest}
                onUpdate={updateDataRequest}
                onExport={() => void runExport()}
              />
            ) : (
              overviewUnavailable
            ),
          },
          {
            id: "notes",
            label: `Notes${overview && overview.notes.length > 0 ? ` (${overview.notes.length})` : ""}`,
            content: overview ? (
              <NotesPanel
                notes={overview.notes}
                busy={busy}
                error={noteError}
                onAdd={addNote}
                onPin={(note) => void pinNote(note)}
              />
            ) : (
              overviewUnavailable
            ),
          },
          {
            id: "history",
            label: "History",
            content: overview ? (
              <TimelinePanel rows={overview.timeline} />
            ) : (
              overviewUnavailable
            ),
          },
        ]}
      />

      <AccountActionDialog
        status={user.account_status}
        open={accountOpen}
        onClose={() => {
          setAccountOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyAccountAction(user.id, input.action, {
              reason: input.reason,
              ...(input.approvalId ? { approval_id: input.approvalId } : {}),
            });
            toast.push({
              title: "Account action applied",
              description: formatLabel(input.action),
              tone: "success",
            });
            setAccountOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ProfileEditDrawer
        open={profileOpen}
        user={user}
        onClose={() => {
          setProfileOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (patch) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.updateProfile(user.id, patch);
            toast.push({ title: "Profile updated", tone: "success" });
            setProfileOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreateRoleExtensionDialog
        open={createRoleOpen}
        onClose={() => {
          setCreateRoleOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async ({ roleType }) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.createRoleExtension(user.id, { role_type: roleType });
            toast.push({ title: "Role extension created", tone: "success" });
            setCreateRoleOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <RoleExtensionActionDialog
        open={roleAction !== null}
        actionLabel={roleAction?.action ?? "action"}
        onClose={() => {
          setRoleAction(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (reason) => {
          if (!roleAction) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.applyRoleExtensionAction(
              user.id,
              roleAction.id,
              roleAction.action,
              { reason },
            );
            toast.push({
              title: "Role extension updated",
              description: formatLabel(roleAction.action),
              tone: "success",
            });
            setRoleAction(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <RequestVerificationDialog
        open={requestVerificationOpen}
        onClose={() => {
          setRequestVerificationOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async ({ verificationType }) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.requestVerification(user.id, {
              verification_type: verificationType,
            });
            toast.push({ title: "Verification requested", tone: "success" });
            setRequestVerificationOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <DecideVerificationDialog
        open={decideVerification !== null}
        onClose={() => {
          setDecideVerification(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          if (!decideVerification) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.decideVerification(user.id, decideVerification.id, {
              action: input.action,
              decision_reason: input.decisionReason,
              ...(input.approvalId ? { approval_id: input.approvalId } : {}),
              ...(input.isOverride !== undefined
                ? { is_override: input.isOverride }
                : {}),
            });
            toast.push({ title: "Verification decision recorded", tone: "success" });
            setDecideVerification(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <MergeUsersDialog
        open={mergeOpen}
        defaultSurvivorId={user.id}
        onClose={() => {
          setMergeOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.mergeUsers({
              survivor_user_id: input.survivorUserId,
              duplicate_user_id: input.duplicateUserId,
              reason: input.reason,
              approval_id: input.approvalId,
            });
            toast.push({
              title: "Users merged",
              description: "Backend preserved survivor lineage.",
              tone: "success",
            });
            setMergeOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

function RoleExtensionsTable({
  rows,
  onAction,
}: {
  rows: RoleExtensionDto[];
  onAction: (id: string, action: RoleExtensionAction) => void;
}) {
  const actions: RoleExtensionAction[] = [
    "activate",
    "restrict",
    "suspend",
    "revoke",
    "remove",
  ];

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Role</Th>
          <Th>Status</Th>
          <Th>Effective from</Th>
          <Th>Effective to</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>{formatLabel(row.role_type)}</Td>
            <Td>{formatLabel(row.status)}</Td>
            <Td>{row.effective_from}</Td>
            <Td>{row.effective_to ?? "—"}</Td>
            <Td>
              <Flex gap={4} wrap>
                {actions.map((action) => (
                  <Button
                    key={action}
                    size="sm"
                    variant="ghost"
                    onClick={() => onAction(row.id, action)}
                  >
                    {formatLabel(action)}
                  </Button>
                ))}
              </Flex>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
